/**
 * CreateBoardDialog — modal for creating a new board from the
 * left rail's "+ New board" affordance. Trello calls these
 * "Create board" — same shape: name, kind/template, visibility.
 *
 * Backed by `KanbanApi.createBoard` (`POST /api/kanban/boards/`).
 * The backend auto-provisions the canonical lane set for the chosen
 * kind on first open via `POST /api/kanban/boards/<id>/provision-lanes/`,
 * so the operator drops into a usable board the moment it's created.
 *
 * DECOMPOSITION (<500-line ceiling): the static choice data + types + the
 * payload helper live in create-board/model.ts, all form state/effects/submit
 * in create-board/useCreateBoardForm.ts, and the clone-from-board section in
 * create-board/CloneFromBoardPicker.tsx. This orchestrator owns the modal shell
 * + field layout. No behaviour change from the former monolith.
 *
 * 2026-05-14 — portal-to-body fix (BUG-2026-05-14-CREATE-BOARD-DIALOG-HIDDEN):
 * the modal is mounted via `createPortal(..., document.body)` rather than
 * rendered inline in its parent's subtree, because a `position: fixed` element
 * is clipped by any ancestor with `transform` / `filter` / `backdrop-filter` /
 * `contain` (which establishes a new containing block for fixed descendants).
 * The SAM AppShell + kanban board canvas chain has multiple such ancestors, so
 * rendered inline this dialog sized itself to the parent's narrow left-rail box
 * → effectively invisible. Portalling to body matches every other kanban
 * overlay (KanbanDuplicatesDialog, KanbanMergeCardsDialog, KanbanContextMenu*).
 */

import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { HardHat, Loader2, X } from 'lucide-react'
import type { BoardVisibility } from '../types'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { BOARD_VISIBILITY_HINTS } from '../utils/boardAccess'
import { BoardInvitePicker } from './BoardInvitePicker'
import { CloneFromBoardPicker } from './create-board/CloneFromBoardPicker'
import { useCreateBoardForm } from './create-board/useCreateBoardForm'
import {
  CONSTRUCTION_QUICK_STARTS,
  DEFAULT_KIND_CHOICES,
  VISIBILITY_LABEL,
  type CreateBoardDialogProps,
} from './create-board/model'

export type { CreateBoardDialogProps, CreateBoardInput } from './create-board/model'
export { buildBoardCreatePayload } from './create-board/model'

export function CreateBoardDialog({
  open,
  onClose,
  organisationId,
  onCreate,
  kindChoices = DEFAULT_KIND_CHOICES,
  loadTemplateCandidates,
  orgMembers,
}: CreateBoardDialogProps) {
  const {
    name, setName,
    kind, setKind,
    visibility, setVisibility,
    description, setDescription,
    invited, setInvited,
    busy,
    error,
    inputRef,
    cloneFromBoardId, setCloneFromBoardId,
    cloneConfig, setCloneConfig,
    templates,
    templatesLoading,
    templatePickerOpen, setTemplatePickerOpen,
    templateFilter, setTemplateFilter,
    quickStart,
    applyQuickStart,
    selectedTemplate,
    handleSubmit,
    slug,
  } = useCreateBoardForm({
    open,
    onClose,
    organisationId,
    onCreate,
    kindChoices,
    loadTemplateCandidates,
  })

  // Escape-to-close already lives in useCreateBoardForm; trap Tab only.
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, { active: open })

  // Portal target — `document.body` so the modal sits at the top of
  // the DOM, escaping any ancestor that may have established a new
  // containing block for `position: fixed` descendants (see the file
  // header for the spec details). Guard against SSR / non-browser
  // environments by short-circuiting when `document` is undefined.
  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-create-board-title"
      data-kanban-overlay="create-board-dialog"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      {/* BUG-2026-06-30-CREATE-BOARD-DIALOG-CLIPPED — the card had no height
          cap and no internal scroll, so on a short viewport it centred and the
          footer ("Create board") clipped off-screen with no way to reach it.
          Cap the card to the viewport (minus the p-4 inset) and make it a flex
          column whose BODY scrolls while the header + footer stay pinned. */}
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 dark:border-neutral-800 px-4 py-3">
          <h2
            id="kb-create-board-title"
            className="text-sm font-semibold text-neutral-900 dark:text-neutral-100"
          >
            Create board
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div>
            <label
              htmlFor="kb-board-name"
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Board name <span className="text-red-500">*</span>
            </label>
            <input
              id="kb-board-name"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
              placeholder="e.g. ATTCORP MFLC"
              disabled={busy}
              className="w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-60"
            />
            {name && (
              <p className="mt-1 text-[10px] text-neutral-400 font-mono">
                URL slug: {slug}
              </p>
            )}
          </div>

          {/* FEAT-2026-06-07-KANBAN-CONSTRUCTION — construction
              quick-starts. One tap pre-fills the form with a
              construction board (name + description + nearest kind).
              Companion backend MR will promote these to first-class
              kinds with canonical lanes. */}
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300">
              <HardHat className="h-3 w-3 text-amber-500" />
              Construction quick-starts
              <span className="font-normal text-neutral-400">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CONSTRUCTION_QUICK_STARTS.map((qs) => (
                <button
                  key={qs.id}
                  type="button"
                  onClick={() => applyQuickStart(qs)}
                  disabled={busy}
                  title={qs.description}
                  data-testid={`kb-construction-quickstart-${qs.id}`}
                  className={[
                    'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                    quickStart === qs.id
                      ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
                      : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-900',
                  ].join(' ')}
                >
                  {qs.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
              Template
            </label>
            <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto rounded-md border border-neutral-200 dark:border-neutral-800 p-1.5">
              {kindChoices.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setKind(c.value)}
                  disabled={busy}
                  className={[
                    'flex items-start gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors',
                    kind === c.value
                      ? 'bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-500'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-900',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="kb-board-kind"
                    checked={kind === c.value}
                    onChange={() => setKind(c.value)}
                    className="mt-0.5"
                    aria-hidden
                    tabIndex={-1}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-neutral-800 dark:text-neutral-100">
                      {c.label}
                    </div>
                    <div className="text-[11px] text-neutral-500">{c.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Clone-from-existing-board section. Only renders when
              the parent supplies `loadTemplateCandidates` so other
              callers (e.g. tests) don't have to wire it. */}
          {loadTemplateCandidates && (
            <CloneFromBoardPicker
              templatePickerOpen={templatePickerOpen}
              setTemplatePickerOpen={setTemplatePickerOpen}
              templates={templates}
              templatesLoading={templatesLoading}
              templateFilter={templateFilter}
              setTemplateFilter={setTemplateFilter}
              cloneFromBoardId={cloneFromBoardId}
              setCloneFromBoardId={setCloneFromBoardId}
              cloneConfig={cloneConfig}
              setCloneConfig={setCloneConfig}
              selectedTemplate={selectedTemplate}
            />
          )}

          <div>
            <label
              htmlFor="kb-board-visibility"
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Visibility
            </label>
            <select
              id="kb-board-visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as BoardVisibility)}
              disabled={busy}
              className="w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            >
              {(Object.keys(VISIBILITY_LABEL) as BoardVisibility[]).map((v) => (
                <option key={v} value={v}>
                  {VISIBILITY_LABEL[v]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] leading-snug text-neutral-500 dark:text-neutral-400">
              {BOARD_VISIBILITY_HINTS[visibility]}
            </p>

            {/* Members-only boards: invite the specific teammates who should
                have access up front. Hidden unless the caller supplies the
                org roster (older callers / tests degrade gracefully — invites
                can still be added later via the board's Access popover). */}
            {visibility === 'BOARD_MEMBERS' && orgMembers && orgMembers.length > 0 && (
              <div className="mt-2 rounded-md border border-neutral-200 p-2 dark:border-neutral-800">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                  Invite team members
                </p>
                <BoardInvitePicker
                  orgMembers={orgMembers}
                  invited={invited}
                  onChange={setInvited}
                  disabled={busy}
                />
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="kb-board-desc"
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Description <span className="text-neutral-400">(optional)</span>
            </label>
            <textarea
              id="kb-board-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short note about what this board tracks…"
              disabled={busy}
              rows={2}
              className="w-full resize-none rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-60"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded px-2 py-1.5">
              {error}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-neutral-200 dark:border-neutral-800 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!name.trim() || busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            Create board
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
